import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi, getAccessToken } from '../../utils/api';
import Card from './shared/Card';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Spinner from './shared/Spinner';
import Modal from './shared/Modal';
import Tabs from './shared/Tabs';
import Pagination from './shared/Pagination';

const STATE_VARIANTS = {
  Running: 'success',
  Pending: 'warning',
  Paused: 'info',
  Stopping: 'warning',
  Terminated: 'outline',
  Failed: 'danger',
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const fmtTTL = (expiresAt) => {
  if (!expiresAt) return '-';
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return 'Expired';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export default function AdminSandbox() {
  const [activeTab, setActiveTab] = useState('manage');

  const tabs = [
    { id: 'manage', label: 'Manage' },
    { id: 'test', label: 'Test Console' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">Sandbox Manager</h1>
      </div>
      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
      {activeTab === 'manage' && <ManageTab />}
      {activeTab === 'test' && <TestConsoleTab />}
    </div>
  );
}

function ManageTab() {
  const [sandboxes, setSandboxes] = useState({ items: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [confirmKill, setConfirmKill] = useState(null);
  const pageSize = 20;

  const loadHealth = useCallback(() => {
    adminApi.sandboxHealth()
      .then(setHealth)
      .catch(() => setHealth({ status: 'unreachable' }));
  }, []);

  const loadSandboxes = useCallback(() => {
    setLoading(true);
    adminApi.listSandboxes({ state: stateFilter, page, pageSize })
      .then(setSandboxes)
      .catch(() => setSandboxes({ items: [], pagination: {} }))
      .finally(() => setLoading(false));
  }, [stateFilter, page]);

  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { loadSandboxes(); }, [loadSandboxes]);

  const doAction = async (id, action) => {
    setActionLoading(`${id}-${action}`);
    try {
      if (action === 'kill') await adminApi.killSandbox(id);
      else if (action === 'pause') await adminApi.pauseSandbox(id);
      else if (action === 'resume') await adminApi.resumeSandbox(id);
      else if (action === 'renew') await adminApi.renewSandbox(id, 30);
      if (selected?.id === id && action === 'kill') setSelected(null);
    } catch (e) {
      console.error(`Sandbox ${action} failed:`, e);
    } finally {
      setActionLoading('');
      setConfirmKill(null);
      loadSandboxes();
    }
  };

  const states = ['', 'Running', 'Pending', 'Paused', 'Terminated', 'Failed'];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {states.map((s) => (
            <button
              key={s || 'all'}
              onClick={() => { setStateFilter(s); setPage(1); }}
              className={`h-7 px-2.5 text-xs rounded-md border transition-colors ${
                stateFilter === s
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className={`h-2.5 w-2.5 rounded-full ${health?.status === 'healthy' ? 'bg-emerald-500' : 'bg-red-500'}`} />
          <span className="text-xs text-gray-500">
            {health?.status === 'healthy' ? 'Online' : 'Offline'}
          </span>
          <Button variant="ghost" size="xs" onClick={() => { loadHealth(); loadSandboxes(); }}>
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <Spinner center text="Loading sandboxes..." />
      ) : sandboxes.items.length === 0 ? (
        <Card><p className="text-sm text-gray-500 text-center py-8">No sandboxes found</p></Card>
      ) : (
        <div className="space-y-2">
          {sandboxes.items.map((sb) => (
            <div
              key={sb.id}
              onClick={() => setSelected(selected?.id === sb.id ? null : sb)}
              className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                selected?.id === sb.id
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant={STATE_VARIANTS[sb.status?.state] || 'outline'}>
                    {sb.status?.state || 'Unknown'}
                  </Badge>
                  <span className="text-xs font-mono text-gray-700 truncate">{sb.id}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {sb.status?.state === 'Running' && (
                    <>
                      <Button variant="ghost" size="xs" loading={actionLoading === `${sb.id}-renew`}
                        onClick={(e) => { e.stopPropagation(); doAction(sb.id, 'renew'); }}>+30m</Button>
                      <Button variant="ghost" size="xs" loading={actionLoading === `${sb.id}-pause`}
                        onClick={(e) => { e.stopPropagation(); doAction(sb.id, 'pause'); }}>Pause</Button>
                    </>
                  )}
                  {sb.status?.state === 'Paused' && (
                    <Button variant="ghost" size="xs" loading={actionLoading === `${sb.id}-resume`}
                      onClick={(e) => { e.stopPropagation(); doAction(sb.id, 'resume'); }}>Resume</Button>
                  )}
                  {['Running', 'Paused', 'Pending'].includes(sb.status?.state) && (
                    <Button variant="danger" size="xs"
                      onClick={(e) => { e.stopPropagation(); setConfirmKill(sb.id); }}>Kill</Button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                <span>Created: {fmtDate(sb.createdAt)}</span>
                <span>TTL: {fmtTTL(sb.expiresAt)}</span>
                {sb.metadata?.name && <span>Name: {sb.metadata.name}</span>}
              </div>
              {selected?.id === sb.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  <DetailRow label="Image" value={sb.image?.uri || '-'} />
                  <DetailRow label="Entrypoint" value={sb.entrypoint?.join(' ') || '-'} />
                  <DetailRow label="Expires" value={fmtDate(sb.expiresAt)} />
                  <DetailRow label="Reason" value={sb.status?.reason || '-'} />
                  {sb.metadata && Object.keys(sb.metadata).length > 0 && (
                    <DetailRow label="Metadata" value={JSON.stringify(sb.metadata)} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {sandboxes.pagination?.totalPages > 1 && (
        <Pagination total={sandboxes.pagination.totalItems} limit={pageSize}
          offset={(page - 1) * pageSize} onChange={(o) => setPage(Math.floor(o / pageSize) + 1)} />
      )}

      <Modal open={!!confirmKill} onClose={() => setConfirmKill(null)} title="Kill Sandbox">
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Kill <span className="font-mono text-gray-900">{confirmKill}</span>? Files in /mnt/user/outputs/ are preserved.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setConfirmKill(null)}>Cancel</Button>
          <Button variant="danger" size="sm" loading={actionLoading === `${confirmKill}-kill`}
            onClick={() => doAction(confirmKill, 'kill')}>Kill</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const SANDBOX_IMAGES = [
  'simplize/code-interpreter:v1.0.2',
  'opensandbox/code-interpreter:v1.0.2',
];

function TestConsoleTab() {
  const [sandboxId, setSandboxId] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedImage, setSelectedImage] = useState(SANDBOX_IMAGES[0]);
  const [command, setCommand] = useState('');
  const [execLoading, setExecLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [filePath, setFilePath] = useState('/');
  const [fileList, setFileList] = useState('');
  const [fileContent, setFileContent] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const logsEndRef = useRef(null);

  const scrollToBottom = () => logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { scrollToBottom(); }, [logs]);

  const addLog = (type, text) => {
    setLogs((prev) => [...prev, { type, text, time: new Date().toLocaleTimeString() }]);
  };

  const handleCreate = async () => {
    setCreating(true);
    addLog('system', `Creating sandbox (${selectedImage})...`);
    try {
      const res = await adminApi.createTestSandbox(selectedImage, 30);
      setSandboxId(res.sandbox_id);
      addLog('success', `Sandbox created: ${res.sandbox_id}`);
    } catch (e) {
      addLog('error', `Create failed: ${e.message || e}`);
    } finally {
      setCreating(false);
    }
  };

  const handleExec = async () => {
    if (!command.trim() || !sandboxId) return;
    const cmd = command.trim();
    setCommand('');
    setExecLoading(true);
    addLog('input', `$ ${cmd}`);
    try {
      const res = await adminApi.execSandboxCommand(sandboxId, cmd);
      if (res.stdout) addLog('stdout', res.stdout);
      if (res.stderr) addLog('stderr', res.stderr);
      if (res.exit_code !== 0 && res.exit_code != null) addLog('error', `exit code: ${res.exit_code}`);
    } catch (e) {
      addLog('error', `Exec failed: ${e.message || e}`);
    } finally {
      setExecLoading(false);
    }
  };

  const handleListFiles = async () => {
    if (!sandboxId) return;
    setFileLoading(true);
    setFileContent(null);
    try {
      const res = await adminApi.listSandboxFiles(sandboxId, filePath);
      setFileList(res.output || res.error || 'Empty');
    } catch (e) {
      setFileList(`Error: ${e.message || e}`);
    } finally {
      setFileLoading(false);
    }
  };

  const handleViewFile = async (path) => {
    if (!sandboxId) return;
    setFileLoading(true);
    try {
      const res = await adminApi.viewSandboxFile(sandboxId, path);
      setFileContent({ path: res.path, content: res.content });
    } catch (e) {
      setFileContent({ path, content: `Error: ${e.message || e}` });
    } finally {
      setFileLoading(false);
    }
  };

  const handleDownload = (path) => {
    if (!sandboxId) return;
    const url = adminApi.sandboxFileDownloadUrl(sandboxId, path);
    const token = getAccessToken();
    window.open(`${url}&token=${token}`, '_blank');
  };

  const handleStop = async () => {
    if (!sandboxId) return;
    addLog('system', 'Killing sandbox...');
    try {
      await adminApi.killSandbox(sandboxId);
      addLog('success', 'Sandbox killed');
      setSandboxId('');
    } catch (e) {
      addLog('error', `Kill failed: ${e.message || e}`);
    }
  };

  const [showExamples, setShowExamples] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Terminal" headerRight={
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="xs" onClick={() => setShowExamples(true)}>Examples</Button>
          {sandboxId ? (
            <>
              <Badge variant="success">Connected</Badge>
              <span className="text-xs font-mono text-gray-500">{sandboxId.slice(0, 12)}...</span>
              <Button variant="danger" size="xs" onClick={handleStop}>Stop</Button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={selectedImage}
                onChange={(e) => setSelectedImage(e.target.value)}
                list="sandbox-images"
                className="h-7 px-2 rounded border border-gray-200 bg-white text-xs font-mono text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400 w-64"
                placeholder="image:tag"
              />
              <datalist id="sandbox-images">
                {SANDBOX_IMAGES.map(img => (
                  <option key={img} value={img} />
                ))}
              </datalist>
              <Button variant="primary" size="xs" loading={creating} onClick={handleCreate}>
                Start
              </Button>
            </>
          )}
        </div>
      } noPadding>
      <ExamplesModal
        open={showExamples}
        onClose={() => { setShowExamples(false); setSelectedExample(null); }}
        selected={selectedExample}
        onSelect={setSelectedExample}
      />
        <div className="bg-gray-950 text-gray-100 font-mono text-xs h-[400px] flex flex-col">
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {logs.length === 0 && (
              <p className="text-gray-500">Click "Start Sandbox" to begin...</p>
            )}
            {logs.map((log, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-gray-600 flex-shrink-0">{log.time}</span>
                <span className={
                  log.type === 'error' ? 'text-red-400' :
                  log.type === 'stderr' ? 'text-yellow-400' :
                  log.type === 'success' ? 'text-emerald-400' :
                  log.type === 'system' ? 'text-blue-400' :
                  log.type === 'input' ? 'text-cyan-400' :
                  'text-gray-100'
                }>
                  <pre className="whitespace-pre-wrap">{log.text}</pre>
                </span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
          <div className="border-t border-gray-800 p-2 flex flex-col gap-1">
            <div className="flex gap-2">
              <span className="text-emerald-400 flex-shrink-0">$</span>
              <textarea
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleExec(); }
                }}
                disabled={!sandboxId || execLoading}
                placeholder={sandboxId ? 'Type command (Shift+Enter for new line)' : 'Start sandbox first'}
                rows={command.includes('\n') ? Math.min(command.split('\n').length, 8) : 1}
                className="flex-1 bg-transparent outline-none text-gray-100 placeholder-gray-600 resize-none font-mono text-xs"
              />
              {execLoading && <Spinner size="sm" />}
            </div>
          </div>
        </div>
      </Card>

      <Card title="File Explorer" noPadding>
        <div className="h-[400px] flex flex-col">
          <div className="border-b border-gray-200 p-2 flex gap-2">
            <input
              type="text"
              value={filePath}
              onChange={(e) => setFilePath(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleListFiles()}
              disabled={!sandboxId}
              placeholder="/path/to/dir"
              className="flex-1 h-7 px-2 text-xs border border-gray-200 rounded-md outline-none"
            />
            <Button variant="secondary" size="xs" disabled={!sandboxId} loading={fileLoading}
              onClick={handleListFiles}>List</Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {fileContent ? (
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-gray-600">{fileContent.path}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="xs" onClick={() => handleDownload(fileContent.path)}>
                      Download
                    </Button>
                    <Button variant="ghost" size="xs" onClick={() => setFileContent(null)}>
                      Back
                    </Button>
                  </div>
                </div>
                <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto max-h-[300px] whitespace-pre-wrap">
                  {fileContent.content}
                </pre>
              </div>
            ) : fileList ? (
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">{filePath}</span>
                </div>
                <div className="space-y-0.5">
                  {fileList.split('\n').filter(Boolean).map((line, i) => {
                    const parts = line.trim().split(/\s+/);
                    const name = parts[parts.length - 1];
                    const isDir = line.startsWith('d');
                    const isTotal = line.startsWith('total');
                    if (isTotal) return null;
                    if (name === '.' || name === '..') return null;
                    const fullPath = filePath.endsWith('/') ? `${filePath}${name}` : `${filePath}/${name}`;
                    return (
                      <div key={i} className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50 text-xs">
                        <button
                          className="flex items-center gap-1.5 text-left font-mono text-gray-700 hover:text-gray-900"
                          onClick={() => isDir ? (setFilePath(fullPath), handleListFiles()) : handleViewFile(fullPath)}
                        >
                          <span>{isDir ? '📁' : '📄'}</span>
                          <span>{name}</span>
                        </button>
                        {!isDir && (
                          <div className="flex gap-1">
                            <button className="text-gray-400 hover:text-gray-700" onClick={() => handleViewFile(fullPath)}>View</button>
                            <button className="text-gray-400 hover:text-gray-700" onClick={() => handleDownload(fullPath)}>DL</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-12">
                {sandboxId ? 'Enter path and click List' : 'Start sandbox first'}
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="text-xs">
      <span className="text-gray-400 w-24 inline-block">{label}</span>
      <span className="text-gray-700 font-mono">{value}</span>
    </div>
  );
}

const EXAMPLES = [
  {
    id: 'readme-python',
    label: 'README: Python',
    icon: '📘',
    steps: [
      { title: 'Install packages', command: 'uv pip install --system --break-system-packages <package>' },
      { title: 'Run inline code', command: 'python3 -c "print(\'hello\')"' },
      { title: 'Run multiline (heredoc)', command: "python3 << 'EOF'\nimport json\ndata = {\"key\": \"value\"}\nprint(json.dumps(data, indent=2))\nEOF" },
      { title: 'Create script file', command: "cat > /workspace/script.py << 'EOF'\nimport json\ndata = {\"message\": \"hello from python\"}\nwith open(\"/mnt/user/outputs/result.json\", \"w\") as f:\n    json.dump(data, f, indent=2)\nprint(\"Saved /mnt/user/outputs/result.json\")\nEOF" },
      { title: 'Run script file', command: 'python3 /workspace/script.py' },
      { title: 'Output files go to', command: 'ls -la /mnt/user/outputs/' },
    ],
  },
  {
    id: 'readme-node',
    label: 'README: Node.js',
    icon: '📗',
    steps: [
      { title: 'Install packages', command: 'cd /workspace && npm install <package>' },
      { title: 'Run inline code', command: 'node -e "console.log(\'hello\')"' },
      { title: 'Create script file', command: "cat > /workspace/app.js << 'EOF'\nconst fs = require(\"fs\");\nconst data = { message: \"hello from node\" };\nfs.writeFileSync(\"/mnt/user/outputs/result.json\", JSON.stringify(data, null, 2));\nconsole.log(\"Saved /mnt/user/outputs/result.json\");\nEOF" },
      { title: 'Run script', command: 'NODE_PATH=/workspace/node_modules node /workspace/app.js' },
      { title: 'Output files go to', command: 'ls -la /mnt/user/outputs/' },
    ],
  },
  {
    id: 'python-excel',
    label: 'Python: Export Excel',
    icon: '🐍',
    steps: [
      { title: 'Install openpyxl', command: 'uv pip install --system --break-system-packages openpyxl' },
      { title: 'Create script', command: "cat > /workspace/excel.py << 'EOF'\nimport openpyxl\n\nwb = openpyxl.Workbook()\nws = wb.active\nws.title = \"Stock Report\"\n\nws.append([\"Ticker\", \"RSI\", \"Price\", \"Trend\"])\nws.append([\"VNM\", 65.2, 72500, \"Bullish\"])\nws.append([\"FPT\", 55.8, 128000, \"Neutral\"])\nws.append([\"VIC\", 38.1, 42300, \"Bearish\"])\n\nfor col in ws.iter_cols(min_row=1, max_row=1):\n    for cell in col:\n        cell.font = openpyxl.styles.Font(bold=True)\n\nwb.save(\"/mnt/user/outputs/report.xlsx\")\nprint(\"Saved /mnt/user/outputs/report.xlsx\")\nEOF" },
      { title: 'Run script', command: 'python3 /workspace/excel.py' },
      { title: 'Verify file', command: 'ls -la /mnt/user/outputs/report.xlsx' },
    ],
  },
  {
    id: 'python-analysis',
    label: 'Python: Data Analysis',
    icon: '📊',
    steps: [
      { title: 'Create analysis script', command: "cat > /workspace/analysis.py << 'EOF'\nimport json, random\n\ntickers = [\"VNM\", \"FPT\", \"VIC\", \"HPG\", \"MWG\"]\nresults = []\nfor t in tickers:\n    price = round(random.uniform(20000, 150000), 0)\n    rsi = round(random.uniform(20, 80), 1)\n    trend = \"Bullish\" if rsi > 50 else \"Bearish\"\n    results.append({\"ticker\": t, \"price\": price, \"rsi\": rsi, \"trend\": trend})\n\nwith open(\"/mnt/user/outputs/analysis.json\", \"w\") as f:\n    json.dump(results, f, indent=2)\n\nfor r in results:\n    print(f\"{r['ticker']}: RSI={r['rsi']} Price={r['price']} {r['trend']}\")\nprint(\"\\nSaved to /mnt/user/outputs/analysis.json\")\nEOF" },
      { title: 'Run script', command: 'python3 /workspace/analysis.py' },
      { title: 'View result', command: 'cat /mnt/user/outputs/analysis.json' },
    ],
  },
  {
    id: 'python-csv',
    label: 'Python: CSV Export',
    icon: '📄',
    steps: [
      { title: 'Create CSV script', command: "cat > /workspace/csv_export.py << 'EOF'\nimport csv\n\nwith open(\"/mnt/user/outputs/stocks.csv\", \"w\", newline=\"\") as f:\n    w = csv.writer(f)\n    w.writerow([\"Date\", \"Ticker\", \"Open\", \"High\", \"Low\", \"Close\", \"Volume\"])\n    w.writerow([\"2024-01-15\", \"VNM\", 71000, 73000, 70500, 72500, 1500000])\n    w.writerow([\"2024-01-15\", \"FPT\", 125000, 129000, 124000, 128000, 2300000])\n    w.writerow([\"2024-01-15\", \"HPG\", 28000, 29500, 27800, 29000, 5600000])\n\nprint(\"Saved /mnt/user/outputs/stocks.csv\")\nEOF" },
      { title: 'Run script', command: 'python3 /workspace/csv_export.py' },
      { title: 'View CSV', command: 'cat /mnt/user/outputs/stocks.csv' },
    ],
  },
  {
    id: 'node-docx',
    label: 'Node.js: Create DOCX',
    icon: '📝',
    steps: [
      { title: 'Install docx', command: 'cd /workspace && npm install docx' },
      { title: 'Create script', command: "cat > /workspace/report.js << 'EOF'\nconst { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, AlignmentType, BorderStyle } = require(\"docx\");\nconst fs = require(\"fs\");\n\nasync function main() {\n  const doc = new Document({\n    sections: [{\n      properties: {},\n      children: [\n        new Paragraph({\n          heading: HeadingLevel.HEADING_1,\n          alignment: AlignmentType.CENTER,\n          children: [new TextRun({ text: \"Stock Analysis Report\", bold: true, size: 48 })],\n        }),\n        new Paragraph({\n          alignment: AlignmentType.CENTER,\n          children: [new TextRun({ text: \"Generated by AI Agent - Q1 2024\", italics: true, color: \"666666\", size: 24 })],\n        }),\n        new Paragraph({ children: [] }),\n        new Table({\n          rows: [\n            new TableRow({\n              children: [\"Ticker\", \"Price\", \"RSI\", \"Trend\"].map(h =>\n                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })\n              ),\n            }),\n            ...[\n              [\"VNM\", \"72,500\", \"65.2\", \"Bullish\"],\n              [\"FPT\", \"128,000\", \"55.8\", \"Neutral\"],\n              [\"HPG\", \"29,000\", \"42.1\", \"Bearish\"],\n            ].map(row =>\n              new TableRow({\n                children: row.map(cell =>\n                  new TableCell({ children: [new Paragraph(cell)] })\n                ),\n              })\n            ),\n          ],\n        }),\n        new Paragraph({ children: [] }),\n        new Paragraph({\n          children: [new TextRun({ text: \"Summary: \", bold: true }), new TextRun(\"VNM shows bullish momentum with RSI above 60. FPT is neutral. HPG is bearish.\")],\n        }),\n      ],\n    }],\n  });\n\n  const buffer = await Packer.toBuffer(doc);\n  fs.writeFileSync(\"/mnt/user/outputs/stock_report.docx\", buffer);\n  console.log(\"Saved /mnt/user/outputs/stock_report.docx\");\n}\n\nmain().catch(console.error);\nEOF" },
      { title: 'Run script', command: 'NODE_PATH=/workspace/node_modules node /workspace/report.js' },
      { title: 'Verify', command: 'ls -la /mnt/user/outputs/stock_report.docx' },
    ],
  },
  {
    id: 'node-pptx',
    label: 'Node.js: Create PPTX',
    icon: '📑',
    steps: [
      { title: 'Install pptxgenjs', command: 'cd /workspace && npm install pptxgenjs' },
      { title: 'Create script', command: "cat > /workspace/slides.js << 'EOF'\nconst pptxgen = require(\"pptxgenjs\");\n\nasync function main() {\n  const pres = new pptxgen();\n  pres.layout = \"LAYOUT_16x9\";\n  pres.title = \"Stock Report\";\n\n  const slide1 = pres.addSlide();\n  slide1.background = { color: \"1E2761\" };\n  slide1.addText(\"Stock Analysis Report\", {\n    x: 1, y: 1.5, w: 8, h: 1.5,\n    fontSize: 36, color: \"FFFFFF\", align: \"center\", bold: true,\n  });\n  slide1.addText(\"Q1 2024  |  Generated by AI Agent\", {\n    x: 1, y: 3, w: 8, h: 0.8,\n    fontSize: 16, color: \"CADCFC\", align: \"center\", italic: true,\n  });\n\n  const slide2 = pres.addSlide();\n  slide2.addText(\"Top Picks\", {\n    x: 0.5, y: 0.3, w: 9, h: 0.8,\n    fontSize: 28, color: \"1E2761\", bold: true,\n  });\n  const rows = [\n    [\"Ticker\", \"Price\", \"RSI\", \"Trend\"],\n    [\"VNM\", \"72,500\", \"65.2\", \"Bullish\"],\n    [\"FPT\", \"128,000\", \"55.8\", \"Neutral\"],\n    [\"HPG\", \"29,000\", \"42.1\", \"Bearish\"],\n  ];\n  slide2.addTable(rows, {\n    x: 0.5, y: 1.3, w: 9,\n    colW: [2, 2.5, 2, 2.5],\n    border: { pt: 1, color: \"E0E0E0\" },\n    fontSize: 14,\n  });\n\n  await pres.writeFile({ fileName: \"/mnt/user/outputs/stock_report.pptx\" });\n  console.log(\"Saved /mnt/user/outputs/stock_report.pptx\");\n}\n\nmain().catch(console.error);\nEOF" },
      { title: 'Run script', command: 'NODE_PATH=/workspace/node_modules node /workspace/slides.js' },
      { title: 'Verify', command: 'ls -la /mnt/user/outputs/stock_report.pptx' },
    ],
  },
  {
    id: 'node-json',
    label: 'Node.js: JSON Processing',
    icon: '🟢',
    steps: [
      { title: 'Create script', command: "cat > /workspace/process.js << 'EOF'\nconst fs = require(\"fs\");\n\nconst data = [\n  { ticker: \"VNM\", prices: [71, 72, 73, 72.5, 74] },\n  { ticker: \"FPT\", prices: [125, 127, 126, 128, 130] },\n  { ticker: \"HPG\", prices: [28, 27.5, 29, 29.5, 30] },\n];\n\nconst result = data.map(d => ({\n  ticker: d.ticker,\n  avg: (d.prices.reduce((a, b) => a + b, 0) / d.prices.length).toFixed(1),\n  min: Math.min(...d.prices),\n  max: Math.max(...d.prices),\n  change: ((d.prices.at(-1) - d.prices[0]) / d.prices[0] * 100).toFixed(2) + \"%\",\n}));\n\nresult.forEach(r =>\n  console.log(`${r.ticker}: avg=${r.avg} range=${r.min}-${r.max} change=${r.change}`)\n);\n\nfs.writeFileSync(\"/mnt/user/outputs/summary.json\", JSON.stringify(result, null, 2));\nconsole.log(\"\\nSaved /mnt/user/outputs/summary.json\");\nEOF" },
      { title: 'Run script', command: 'NODE_PATH=/workspace/node_modules node /workspace/process.js' },
      { title: 'View result', command: 'cat /mnt/user/outputs/summary.json' },
    ],
  },
  {
    id: 'system-info',
    label: 'System: Environment Info',
    icon: '🖥️',
    steps: [
      { title: 'Check Python', command: 'python3 --version' },
      { title: 'Check Node', command: 'node --version && npm --version' },
      { title: 'Check disk & memory', command: 'df -h /mnt/user/outputs && free -h' },
      { title: 'Check installed tools', command: 'which python3 node npm uv git curl' },
    ],
  },
];

const SANDBOX_SYSTEM_PROMPT_TEMPLATE = `## Sandbox Environment

You have access to a Linux sandbox (Ubuntu 24) with Python 3.12, Node.js v22, Java 21, Go 1.25, TypeScript, and Bash.

**File locations:**
- Working directory: \`/workspace/\`
- Final outputs: \`/mnt/user/outputs/\` — files saved here are available for user download

**Package management:**
- Python: \`uv pip install --system --break-system-packages <package>\`
- Node.js: \`cd /workspace && npm install <package>\`

**Running scripts:**
- Python: \`python3 /workspace/script.py\`
- Node.js: \`NODE_PATH=/workspace/node_modules node /workspace/script.js\`

**Workflow:**
1. Create script file in \`/workspace/\` using write_file or execute with cat/heredoc
2. Run the script using execute
3. Output files MUST be saved to \`/mnt/user/outputs/\` for user to download

**File creation triggers:**
- "write a document/report" → .docx (python-docx or Node docx)
- "create a presentation" → .pptx (Node pptxgenjs)
- "make a spreadsheet" → .xlsx (openpyxl)
- "export CSV/JSON" → save to /mnt/user/outputs/

**Available skills:**
| Type | Python package | Node.js package |
|------|---------------|-----------------|
| Excel (.xlsx) | openpyxl | exceljs |
| Word (.docx) | python-docx | docx |
| PowerPoint (.pptx) | python-pptx | pptxgenjs |
| PDF | weasyprint, fpdf2 | pdfkit |
| CSV/JSON | built-in | built-in |
| Charts | matplotlib, plotly | chart.js |`;

function ExamplesModal({ open, onClose, selected, onSelect }) {
  const [tab, setTab] = useState('examples');
  const example = EXAMPLES.find(e => e.id === selected);

  const tabs = [
    { id: 'guide', label: 'Guide' },
    { id: 'examples', label: 'Examples' },
    { id: 'system-prompt', label: 'System Prompt' },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Sandbox Reference" size="xl">
      <div className="border-b border-gray-200 px-4">
        <div className="flex gap-4">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`py-2 text-sm border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-gray-900 text-gray-900 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'guide' && (
        <div className="h-[500px] overflow-y-auto p-5 space-y-5 text-sm text-gray-700">
          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Sandbox Overview</h3>
            <p className="text-gray-600 mb-2">
              Sandbox is an isolated Linux container (Ubuntu 24) with Python, Node.js, Java, Go, TypeScript, Bash pre-installed.
              Each sandbox is ephemeral — destroyed when stopped. Files in <code className="text-xs bg-gray-100 px-1 rounded">/mnt/user/outputs/</code> persist on host after sandbox dies.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Directory Structure</h3>
            <pre className="text-xs font-mono bg-gray-950 text-gray-100 rounded-md p-3">{
`/workspace/              Working directory (CWD)
├── node_modules/        npm packages installed here
├── script.py            Your Python scripts
└── app.js               Your Node.js scripts

/mnt/user/outputs/       Persistent output (Volume Mount → host)
├── report.xlsx          Files here survive sandbox kill
└── chart.png            User can download these`
            }</pre>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Python</h3>
            <div className="space-y-2">
              <div>
                <span className="text-xs font-medium text-gray-500">Install packages</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">pip install &lt;package&gt;</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Run inline</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">python3 -c "print('hello')"</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Create + run file</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">{`cat > /workspace/script.py << 'EOF'
print("hello")
EOF
python3 /workspace/script.py`}</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Common packages</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">openpyxl, python-docx, python-pptx, matplotlib, plotly, pandas, numpy, requests</pre>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Node.js</h3>
            <div className="space-y-2">
              <div>
                <span className="text-xs font-medium text-gray-500">Install packages</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">npm install &lt;package&gt;</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Run inline</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">node -e "console.log('hello')"</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Create + run file</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">{`cat > /workspace/app.js << 'EOF'
console.log("hello");
EOF
NODE_PATH=/workspace/node_modules node /workspace/app.js`}</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Common packages</span>
                <pre className="text-xs font-mono bg-gray-100 rounded px-2 py-1 mt-0.5">docx, pptxgenjs, exceljs, sharp, puppeteer, express</pre>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Output Files</h3>
            <div className="space-y-1 text-xs text-gray-600">
              <p>Save files to <code className="bg-gray-100 px-1 rounded">/mnt/user/outputs/</code> for user download.</p>
              <p>This directory is Volume Mounted to host — files persist even after sandbox is killed.</p>
              <p>Use File Explorer (right panel) to browse, view, and download files.</p>
            </div>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">File Type Cheatsheet</h3>
            <table className="w-full text-xs border border-gray-200 rounded">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-1.5 border-b border-gray-200">Type</th>
                  <th className="text-left px-3 py-1.5 border-b border-gray-200">Python</th>
                  <th className="text-left px-3 py-1.5 border-b border-gray-200">Node.js</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {[
                  ['Excel .xlsx', 'openpyxl', 'exceljs'],
                  ['Word .docx', 'python-docx', 'docx'],
                  ['PowerPoint .pptx', 'python-pptx', 'pptxgenjs'],
                  ['PDF', 'fpdf2, weasyprint', 'pdfkit'],
                  ['CSV', 'csv (built-in)', 'fs (built-in)'],
                  ['JSON', 'json (built-in)', 'fs (built-in)'],
                  ['Charts/Images', 'matplotlib, plotly', 'sharp, chart.js'],
                ].map(([type, py, node], i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-1.5 font-sans font-medium text-gray-700">{type}</td>
                    <td className="px-3 py-1.5 text-gray-600">{py}</td>
                    <td className="px-3 py-1.5 text-gray-600">{node}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}

      {tab === 'examples' && (
        <div className="flex h-[500px]">
          <div className="w-56 border-r border-gray-200 overflow-y-auto flex-shrink-0">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.id}
                onClick={() => onSelect(ex.id)}
                className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 transition-colors ${
                  selected === ex.id
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{ex.icon}</span>
                {ex.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {example ? (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{example.icon} {example.label}</h3>
                {example.steps.map((step, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">
                        Step {i + 1}: {step.title}
                      </span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(step.command); }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Copy
                      </button>
                    </div>
                    <pre className="text-xs font-mono bg-gray-950 text-gray-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
                      {step.command}
                    </pre>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-20">Select an example from the left</p>
            )}
          </div>
        </div>
      )}

      {tab === 'system-prompt' && (
        <div className="h-[500px] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="text-sm text-gray-600">System prompt template for sandbox-enabled agents</span>
            <button
              onClick={() => { navigator.clipboard.writeText(SANDBOX_SYSTEM_PROMPT_TEMPLATE); }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50"
            >
              Copy All
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <pre className="text-xs font-mono bg-gray-950 text-gray-100 rounded-md p-4 whitespace-pre-wrap">
              {SANDBOX_SYSTEM_PROMPT_TEMPLATE}
            </pre>
          </div>
        </div>
      )}
    </Modal>
  );
}
